import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable, from, of, throwError } from 'rxjs';
import { concatMap, mergeMap, toArray, timeout, catchError } from 'rxjs/operators';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CasingDefinition } from '../../models/casing-definition.model';
import { Pagination } from '../../models/response.model';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { DealerState } from '../../../../shared/state/DealerState';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-casing-definition',
  templateUrl: './casing-definition.component.html',
  styleUrl: './casing-definition.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingDefinitionComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingDefinition>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefService = inject(ColumnPreferencesService);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  makeList: string[] = [];
  seriesList: string[] = [];
  sizeList: string[] = [];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;

  casingDefinitions: CasingDefinition[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;

  tableConfig: TableConfig<CasingDefinition> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};
  isExporting = false;
  private dialog = inject(MatDialog);

  exportDataFetcher: () => Observable<ExportResponse> = () => {
    const pageSize = 1000; // tune this value according to server capabilities
    const filters = this.filterFormBackend.value;

    return this.productDataService.getCasingDefinitions(filters, 1, pageSize).pipe(
      concatMap((firstResp: any) => {
        const total = firstResp?.data?.pagination?.totalRecords ?? ((firstResp.data.casingDefinitions || []).length || 0);
        const cappedTotal = Math.min(total, 10000); // enforce 10,000 record cap for exports
        const totalPages = Math.max(1, Math.ceil(cappedTotal / pageSize));

        const pageObservables = [] as Observable<any>[];
        pageObservables.push(of(firstResp));
        for (let p = 2; p <= totalPages; p++) {
          pageObservables.push(this.productDataService.getCasingDefinitions(filters, p, pageSize));
        }

        // fetch pages with limited concurrency, collect arrays, flatten and map to export rows
        return from(pageObservables).pipe(
          mergeMap((obs) => obs, 3), // concurrency: 3 parallel requests
          map((r: any) => (r?.data?.casingDefinitions ?? []) as CasingDefinition[]),
          toArray(),
          map((chunks: CasingDefinition[][]) => ([] as CasingDefinition[]).concat(...chunks)),
          map((all: CasingDefinition[]) => ({
            data: all.slice(0, cappedTotal).map((item) => ({
              make: item.make,
              series: item.series,
              size: item.size,
              loadIndex: item.loadIndex,
              speedIndex: item.speedIndex,
              loadRange: item.loadRange,
              avgWt: item.avgWt
            }))
          }))
        );
      }),
      // overall timeout for the export operation
      timeout(30000),
      catchError((err) => {
        // convert timeout errors to a clearer message
        if (err && err.name === 'TimeoutError') {
          console.error('Export timed out after 30 seconds', err);
          return throwError(() => new Error('Export timed out after 30 seconds'));
        }
        console.error('Export failed', err);
        return throwError(() => err);
      })
    );
  };

  fetcher: PaginatorFetcher<CasingDefinition> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getCasingDefinitions({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.casingDefinitions = response.data.casingDefinitions || [];
        this.pagination = response.data.pagination;
        return { items: this.casingDefinitions, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      make: [[]],
      series: [[]],
      size: [[]]
    });
    this.filterFormBackend = this.fb.group({
      make: [[]],
      series: [[]],
      size: [[]]
    });
  }

  ngOnInit(): void {
    this.makeList = this.store.selectSnapshot(ProductsState.products).makeCodes;
    this.seriesList = this.store.selectSnapshot(ProductsState.products).series;
    this.sizeList = this.store.selectSnapshot(ProductsState.products).sizes;
    this.filterForm.patchValue({
      make: [],
      series: [],
      size: []
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // build canonical columns (used as source of truth)
      this.canonicalColumns = {
        make: { cell: (row) => row.make, header: this.translationService.translate('makeText') },
        series: { cell: (row) => row.series, header: this.translationService.translate('seriesText') },
        size: { cell: (row) => row.size, header: this.translationService.translate('sizeText') },
        loadIndex: { cell: (row) => row.loadIndex, header: 'LI' },
        speedIndex: { cell: (row) => row.speedIndex, header: 'SI' },
        loadRange: { cell: (row) => row.loadRange, header: 'LR' },
        avgWt: { cell: (row) => (row.avgWt ?? '').toString(), header: this.translationService.translate('avgWtText') }
      };

      this.columnNames = {
        make: this.translationService.translate('makeText'),
        series: this.translationService.translate('seriesText'),
        size: this.translationService.translate('sizeText'),
        loadIndex: 'LI',
        speedIndex: 'SI',
        loadRange: 'LR',
        avgWt: this.translationService.translate('avgWtText')
      };

      // apply user preferences (localStorage) and initialize tableConfig.columns
      const init = this.columnPrefsHelper.initialize('casing-definition', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingDefinition-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  clearFilters(): void {
    this.filterForm.reset({
      make: [],
      series: [],
      size: []
    });
  }

  private generateFilterForm(): void {
    const filterMake = this.filterForm.get('make').value;
    const filterSeries = this.filterForm.get('series').value;
    const filterSize = this.filterForm.get('size').value;

    this.filterFormBackend.patchValue({
      make: filterMake.length > 0 && filterMake.length === this.makeList.length ? [] : filterMake,
      series: filterSeries.length > 0 && filterSeries.length === this.seriesList.length ? [] : filterSeries,
      size: filterSize.length > 0 && filterSize.length === this.sizeList.length ? [] : filterSize
    });
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'casing-definition',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }
}
